import { useMemo, useState } from 'react'
import { Link, useParams, useSearchParams } from 'react-router'
import { ArrowLeft, ArrowRight, CalendarCheck2, Mail, MessageCircle, Phone, PhoneCall, Search, type LucideIcon } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { ErrorState, LoadingState } from '@/components/composites/states'
import { cn } from '@/lib/utils'
import { paths } from '@/routes/paths'
import { crm2Api, OUTCOMES, useCrm2Contact, useCrm2Meta, useCrm2Outreach, useCrm2Write, type Outcome, type QueueFilter, type QueueItem } from './api'
import { BookDemo, dayOf, timeOf } from './BookDemo'
import { Crm2Frame, Field, fieldClass, KIND_LABEL, LINK_CHIP, usePeopleNames, when, whatsappLink } from './shared'

const FILTERS: Array<[QueueFilter, string]> = [
  ['todo', 'To do'],
  ['callbacks', 'Call-backs'],
  ['booked', 'Booked'],
  ['all', 'Everyone'],
]
const CHANNELS: Array<['call' | 'email' | 'whatsapp', string, LucideIcon]> = [
  ['call', 'Call', Phone],
  ['email', 'Email', Mail],
  ['whatsapp', 'WhatsApp', MessageCircle],
]

/** Tomorrow at 10:00, local, for the call-back field. */
const tomorrowTen = () => {
  const d = new Date(Date.now() + 86_400_000)
  d.setHours(10, 0, 0, 0)
  return new Date(d.getTime() - d.getTimezoneOffset() * 60_000).toISOString().slice(0, 16)
}

/**
 * Outreach: marketing works through the contacts one at a time. Each person shows who they are,
 * their history and their deal; log how the reach-out went, or book the demo there and then.
 * Saving moves on to the next person.
 */
export function OutreachPage() {
  const [params, setParams] = useSearchParams()
  const filter = (params.get('filter') as QueueFilter | null) ?? 'todo'
  const [q, setQ] = useState('')
  const [now] = useState(() => Date.now())
  const queue = useCrm2Outreach(filter, q.trim())
  const items = useMemo(() => queue.data?.items ?? [], [queue.data])
  const picked = params.get('c')
  const set = (patch: Record<string, string | null>) => {
    const next = new URLSearchParams(params)
    for (const [k, v] of Object.entries(patch)) {
      if (v) next.set(k, v)
      else next.delete(k)
    }
    setParams(next, { replace: true })
  }
  const index = Math.max(0, items.findIndex((i) => i.contactId === picked))
  const current: QueueItem | undefined = items.find((i) => i.contactId === picked) ?? items[0]
  const goNext = () => {
    const n = items[index + 1] ?? items[index - 1]
    if (n) set({ c: n.contactId })
  }

  return (
    <Crm2Frame title="Outreach" description="Work through your contacts one at a time: reach out, log how it went, and book the demo while you have them.">
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <div className="inline-flex rounded-lg border border-border bg-surface p-0.5 text-sm" role="group" aria-label="List">
          {FILTERS.map(([k, label]) => (
            <button key={k} type="button" aria-pressed={filter === k} onClick={() => set({ filter: k === 'todo' ? null : k, c: null })} className={cn('rounded-md px-3 py-1', filter === k ? 'bg-accent font-medium' : 'text-muted-foreground')}>
              {label}
            </button>
          ))}
        </div>
        <div className="relative">
          <Search className="pointer-events-none absolute left-2 top-2.5 size-4 text-text-muted" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name, company, email or number" className="h-9 w-72 pl-8" aria-label="Search contacts" />
        </div>
        {queue.data ? <span className="text-sm text-muted-foreground">{queue.data.total} {queue.data.total === 1 ? 'person' : 'people'}</span> : null}
      </div>
      {queue.error ? (
        <ErrorState error={queue.error} onRetry={() => void queue.refetch()} />
      ) : queue.isPending ? (
        <LoadingState label="Loading the outreach list" />
      ) : !items.length ? (
        <p className="rounded-xl border border-border bg-card px-4 py-6 text-sm text-muted-foreground">{filter === 'todo' ? 'Nobody left to reach out to. Nice work.' : 'Nobody here.'}</p>
      ) : (
        <div className="grid min-h-[32rem] gap-4 lg:grid-cols-[20rem_minmax(0,1fr)]">
          <ul className="max-h-[calc(100vh-15rem)] divide-y divide-border-soft overflow-y-auto rounded-xl border border-border bg-card shadow-sm">
            {items.map((i) => (
              <li key={i.contactId}>
                <button type="button" onClick={() => set({ c: i.contactId })} aria-current={current?.contactId === i.contactId} className={cn('grid w-full gap-0.5 px-3 py-2 text-left', current?.contactId === i.contactId ? 'bg-accent' : 'hover:bg-muted/60')}>
                  <span className="truncate text-sm font-medium">{i.name}</span>
                  <span className="truncate text-xs text-muted-foreground">{i.accountName}</span>
                  <QueueHint item={i} now={now} />
                </button>
              </li>
            ))}
          </ul>
          {current ? <Person key={current.contactId} item={current} position={`${index + 1} of ${items.length}`} onPrev={index > 0 ? () => set({ c: items[index - 1]!.contactId }) : undefined} onNext={index < items.length - 1 ? () => set({ c: items[index + 1]!.contactId }) : undefined} onDone={goNext} /> : null}
        </div>
      )}
    </Crm2Frame>
  )
}

function QueueHint({ item, now }: { item: QueueItem; now: number }) {
  if (item.bookedAt) return <span className="text-tiny font-medium text-success">Demo {dayOf(item.bookedAt)}, {timeOf(item.bookedAt)}</span>
  if (item.callbackAt) {
    const due = Date.parse(item.callbackAt) <= now
    return <span className={cn('text-tiny font-medium', due ? 'text-warning' : 'text-text-secondary')}>Call back {when(item.callbackAt)}</span>
  }
  if (!item.lastTouch) return <span className="text-tiny text-info">Not tried yet</span>
  return (
    <span className="truncate text-tiny text-text-muted">
      {item.attempts} {item.attempts === 1 ? 'try' : 'tries'} · last {when(item.lastTouch.at)}
      {item.lastTouch.outcome ? ` · ${item.lastTouch.outcome}` : ''}
    </span>
  )
}

function Person({ item, position, onPrev, onNext, onDone }: { item: QueueItem; position: string; onPrev?: () => void; onNext?: () => void; onDone: () => void }) {
  const { workspaceSlug = '' } = useParams()
  const detail = useCrm2Contact(item.contactId)
  const meta = useCrm2Meta()
  const name = usePeopleNames()
  const [mode, setMode] = useState<'log' | 'book'>('log')
  const [channel, setChannel] = useState<'call' | 'email' | 'whatsapp'>('call')
  const [outcome, setOutcome] = useState<Outcome>('no_answer')
  const [note, setNote] = useState('')
  const [callbackAt, setCallbackAt] = useState(tomorrowTen)
  const log = useCrm2Write(crm2Api.logAttempt, () => 'Logged')
  const wa = whatsappLink(item.phone)
  const stage = item.deal ? (meta.data?.pipelines.find((p) => p.key === item.deal!.pipeline)?.stages.find((s) => s.key === item.deal!.stage)?.label ?? item.deal.stage) : null

  const save = () =>
    log.mutate(
      { contactId: item.contactId, channel, outcome, note: note.trim() || null, callbackAt: outcome === 'call_back' && callbackAt ? new Date(callbackAt).toISOString() : null },
      { onSuccess: () => onDone() },
    )

  return (
    <div className="grid content-start gap-4">
      <section className="rounded-xl border border-border bg-card p-4 shadow-sm">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="grid gap-1">
            <h2 className="text-lg font-semibold">{item.name}</h2>
            <p className="text-sm text-muted-foreground">
              {item.title ? `${item.title} · ` : ''}
              <Link className={LINK_CHIP} to={paths.crm2Account(workspaceSlug, item.accountId)}>
                {item.accountName}
              </Link>
            </p>
            <div className="mt-1 flex flex-wrap gap-2 text-sm">
              {item.phone ? (
                <a className={LINK_CHIP} href={`tel:${item.phone}`}>
                  <PhoneCall className="size-3.5" /> {item.phone}
                </a>
              ) : (
                <span className="text-xs text-text-muted">No number</span>
              )}
              {wa ? (
                <a className={LINK_CHIP} href={wa} target="_blank" rel="noreferrer">
                  <MessageCircle className="size-3.5" /> WhatsApp
                </a>
              ) : null}
              {item.email ? (
                <a className={LINK_CHIP} href={`mailto:${item.email}`}>
                  <Mail className="size-3.5" /> {item.email}
                </a>
              ) : (
                <span className="text-xs text-text-muted">No email</span>
              )}
              <Link className={LINK_CHIP} to={paths.crm2Contact(workspaceSlug, item.contactId)}>
                Contact page
              </Link>
            </div>
          </div>
          <div className="grid justify-items-end gap-1 text-right text-xs text-muted-foreground">
            <span>{position}</span>
            <div className="flex gap-1">
              <Button size="sm" variant="outline" onClick={onPrev} disabled={!onPrev} aria-label="Previous person">
                <ArrowLeft className="size-4" />
              </Button>
              <Button size="sm" variant="outline" onClick={onNext} disabled={!onNext} aria-label="Next person">
                <ArrowRight className="size-4" />
              </Button>
            </div>
            {stage ? <span className="rounded-full bg-muted px-2 py-0.5 text-tiny font-medium text-text-secondary">Deal: {stage}{item.deal?.ownerId ? ` · ${name(item.deal.ownerId)}` : ''}</span> : <span className="text-tiny">No open deal yet</span>}
            {item.bookedAt ? <span className="text-tiny font-medium text-success">Demo booked {dayOf(item.bookedAt)}, {timeOf(item.bookedAt)}</span> : null}
          </div>
        </div>
      </section>

      <section className="rounded-xl border border-border bg-card shadow-sm">
        <div className="flex gap-1 border-b border-border-soft px-3 pt-2" role="tablist">
          <button type="button" role="tab" aria-selected={mode === 'log'} onClick={() => setMode('log')} className={cn('-mb-px border-b-2 px-3 py-2 text-sm', mode === 'log' ? 'border-brand font-medium' : 'border-transparent text-muted-foreground')}>
            Log a reach-out
          </button>
          <button type="button" role="tab" aria-selected={mode === 'book'} onClick={() => setMode('book')} className={cn('-mb-px flex items-center gap-1 border-b-2 px-3 py-2 text-sm', mode === 'book' ? 'border-brand font-medium' : 'border-transparent text-muted-foreground')}>
            <CalendarCheck2 className="size-4" /> Book a demo
          </button>
        </div>
        <div className="p-4">
          {mode === 'book' ? (
            <BookDemo contactId={item.contactId} contactName={item.name.split(' ')[0] ?? item.name} hasEmail={Boolean(item.email)} onBooked={() => onDone()} />
          ) : (
            <div className="grid gap-3 text-sm">
              <div className="flex flex-wrap gap-2" role="group" aria-label="How">
                {CHANNELS.map(([k, label, Icon]) => (
                  <button key={k} type="button" aria-pressed={channel === k} onClick={() => setChannel(k)} className={cn('flex items-center gap-1.5 rounded-lg border px-3 py-1.5', channel === k ? 'border-brand bg-accent font-medium' : 'border-border bg-background hover:border-brand')}>
                    <Icon className="size-4" /> {label}
                  </button>
                ))}
              </div>
              <div className="grid gap-3 sm:grid-cols-2">
                <Field label="How it went">
                  <select className={fieldClass} value={outcome} onChange={(e) => setOutcome(e.target.value as Outcome)} aria-label="How it went">
                    {OUTCOMES.map(([k, label]) => (
                      <option key={k} value={k}>
                        {label}
                      </option>
                    ))}
                  </select>
                </Field>
                {outcome === 'call_back' ? (
                  <Field label="Call back on" hint="Becomes a task for you on Today.">
                    <input type="datetime-local" className={fieldClass} value={callbackAt} onChange={(e) => setCallbackAt(e.target.value)} />
                  </Field>
                ) : null}
              </div>
              <Field label="Notes (optional)">
                <Textarea rows={3} value={note} onChange={(e) => setNote(e.target.value)} maxLength={4000} placeholder="What was said, what they need, who else to talk to" />
              </Field>
              <div className="flex flex-wrap gap-2">
                <Button onClick={save} disabled={log.isPending || (outcome === 'call_back' && !callbackAt)}>
                  {log.isPending ? 'Saving…' : 'Save and next'}
                </Button>
                {outcome === 'talked' ? (
                  <Button variant="outline" onClick={() => setMode('book')}>
                    They agreed: book the demo
                  </Button>
                ) : null}
              </div>
            </div>
          )}
        </div>
      </section>

      <section className="rounded-xl border border-border bg-card shadow-sm">
        <h3 className="border-b border-border-soft px-4 py-2.5 text-sm font-semibold">History</h3>
        {detail.isPending ? (
          <p className="px-4 py-3 text-sm text-muted-foreground">Loading…</p>
        ) : !detail.data?.history.length ? (
          <p className="px-4 py-3 text-sm text-muted-foreground">Nothing logged with {item.name.split(' ')[0]} yet.</p>
        ) : (
          <ul className="max-h-96 divide-y divide-border-soft overflow-y-auto">
            {detail.data.history.slice(0, 50).map((a) => (
              <li key={a.id} className="grid gap-0.5 px-4 py-2 text-sm">
                <span className="flex flex-wrap items-center gap-2">
                  <span className="rounded bg-muted px-1.5 text-tiny font-medium text-text-secondary">{KIND_LABEL[a.kind] ?? a.kind}</span>
                  <span className="font-medium">{a.subject ?? ''}</span>
                  <span className="text-xs text-text-muted">
                    {when(a.occurredAt)}
                    {a.authorId ? ` · ${name(a.authorId)}` : ''}
                  </span>
                </span>
                {a.body ? <span className="whitespace-pre-wrap text-xs text-muted-foreground">{a.body.slice(0, 600)}</span> : null}
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  )
}
